import * as React from 'react';

import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Snackbar from '@mui/material/Snackbar';

import { type User } from '../../../../auth';
import Log from '../../../../Logger';
import DeviceNameDialog from './DisplayNameDialog';
import { Box, Typography } from '@mui/material';
import { FeatureContext } from '../../../FeatureContext';

type Props = {
    user: User;
};

type SnackbarState = {
    isOpen: boolean;
    message: string;
    severity: 'success' | 'error';
};

/**
 * 設定画面におけるデバイス設定コンポーネントです。
 * デバイス表示名変更ダイアログの起動およびスナックバー通知を行います
 * 
 * @param props - コンポーネントのプロパティ
 * @returns JSX.Element - レンダリング可能なSelectコンポーネント
 */
export default function DeviceSettings(props: Props) {
    const logger = Log.getLogger();
    const features = React.useContext(FeatureContext);
    const [isDeviceNameDialogOpen, setIsDeviceNameDialogOpen] = React.useState(false);

    // スナックバー状態を統合
    const [snackbar, setSnackbar] = React.useState<SnackbarState>({
        isOpen: false,
        message: '',
        severity: 'success'
    });

    // ダイアログ開閉処理
    const openDeviceNameDialog = () => {
        logger.ui.info('Settings', 'openDeviceNameDialog');
        setIsDeviceNameDialogOpen(true);
    };

    const closeDeviceNameDialog = () => {
        logger.ui.info('Settings', 'closeDeviceNameDialog');
        setIsDeviceNameDialogOpen(false);
    };

    // スナックバー表示処理
    const showSnackbar = (message: string, severity: 'success' | 'error') => {
        setSnackbar({ isOpen: true, message, severity });
    };

    const closeSnackbar = () => {
        setSnackbar(prev => ({ ...prev, isOpen: false }));
    };

    return (
        <>
            <List
                sx={{ width: '100%', bgcolor: 'background.paper' }}
                subheader={<ListSubheader
                    sx={{ fontSize: '1.5rem', fontWeight: 'bold', mt: 2 }} // フォントサイズを調整
                >
                    デバイス設定
                </ListSubheader>
                }
            >
                <Box component="section" marginLeft={3} marginBottom={1}>
                    <Typography color="text.secondary" variant="body2">
                        クラウドサービス上の表示名を設定するには管理者権限が必要です
                    </Typography>
                </Box>
                <ListItem
                    divider={true}
                >
                    <ListItemIcon sx={{ color: 'inherit', marginLeft: '25px' }}>
                        {<SettingsOutlinedIcon />}
                    </ListItemIcon>
                    <ListItemText primary={'クラウドサービス上のデバイス表示名を設定する'} />
                    <Button
                        onClick={openDeviceNameDialog}
                        sx={{
                            backgroundColor: 'primary.main',
                            color: 'white',
                            '&:hover': {
                                backgroundColor: 'primary.dark',
                            }
                        }}
                    >
                        {/* 権限がない場合は「表示」、ある場合は「設定」と表示 */}
                        {features.canUpdateDeviceDisplayName() ? '設定' : '表示'}
                    </Button>
                </ListItem>
            </List>
            <DeviceNameDialog
                open={isDeviceNameDialogOpen}
                onClose={closeDeviceNameDialog}
                onSuccess={(message) => showSnackbar(message, 'success')}
                onError={(message) => showSnackbar(message, 'error')}
            />
            <Snackbar
                open={snackbar.isOpen}
                onClose={closeSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={closeSnackbar}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};